import { lazy } from "react";

const RELOAD_KEY = "wlj-chunk-reload-at";
const RELOAD_PARAM = "wlj_refresh";
const COOLDOWN_MS = 20000;

export function isChunkLoadError(error) {
  const name = error?.name || "";
  const message = String(error?.message || error || "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk .+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

function rememberReload() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return true;
  } catch (e) {
    return false;
  }
}

/** One navigation onto a fresh document. Returns true when a reload was started. */
export function recoverFromChunkError(error) {
  if (process.env.NODE_ENV !== "production") return false;
  if (typeof window === "undefined") return false;
  if (!isChunkLoadError(error)) return false;
  if (!rememberReload()) return false;

  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_PARAM, String(Date.now()));
  window.location.replace(url.toString());
  return true;
}

export function forceRefresh() {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch (e) {
    /* ignore */
  }
  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_PARAM, String(Date.now()));
  window.location.replace(url.toString());
}

/** Drop the one-shot guard after a successful boot so a later deploy can recover. */
export function finishChunkRecovery() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(RELOAD_KEY);
    const url = new URL(window.location.href);
    if (!url.searchParams.has(RELOAD_PARAM)) return;
    url.searchParams.delete(RELOAD_PARAM);
    window.history.replaceState(
      window.history.state,
      "",
      url.pathname + url.search + url.hash
    );
  } catch (e) {
    /* ignore */
  }
}

export function lazyWithRetry(factory) {
  return lazy(() =>
    factory().catch((error) => {
      if (recoverFromChunkError(error)) {
        return new Promise(() => {});
      }
      throw error;
    })
  );
}
